import type { ReactNode } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { dateTimeNoSeconds, fullDateTime } from './dateTime';

/**
 * Wspólne klocki list — żeby tabela mieściła się bez przewijania w prawo.
 *
 * Operator pracuje na 1366 px z otwartym menu (28.09.2026: „popraw widoki,
 * żeby nic się nie rozjeżdżało; denerwuje przewijanie w prawo”). Zasada:
 * informacji nie usuwamy, tylko skracamy — długi tekst kończy się
 * wielokropkiem, a pełna treść jest w podpowiedzi.
 *
 * Szerokość w tabeli o automatycznym układzie trzeba ograniczyć jawnie:
 * komórka z `nowrap` bez `maxWidth` rozciąga się do pełnej długości tekstu
 * i wypycha tabelę poza ekran — czyli robi dokładnie to, co mamy usunąć.
 */

/** Szerokość tekstu w komórce: węziej na laptopie, szerzej na dużym monitorze. */
export type CellWidth = number | { xs?: number; md?: number; lg?: number; xl?: number };

/**
 * Tekst w jednej linii z wielokropkiem i pełną treścią w podpowiedzi.
 *
 * Podpowiedź dostaje pełny tekst zawsze, nie tylko gdy się nie zmieścił —
 * pomiar szerokości w każdej komórce listy kosztowałby więcej, niż daje.
 */
export const ClampedText = ({
  text,
  maxWidth = 280,
  bold = false,
  variant = 'body2',
  color,
}: {
  text: string | null | undefined;
  maxWidth?: CellWidth;
  bold?: boolean;
  variant?: 'body2' | 'caption';
  color?: string;
}) => {
  const value = text && text.trim() !== '' ? text : null;
  if (!value) {
    return (
      <Typography variant={variant} color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={value} enterDelay={400}>
      <Typography
        variant={variant}
        noWrap
        color={color}
        sx={{ display: 'block', maxWidth, fontWeight: bold ? 600 : undefined }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

/**
 * Główny tekst i dopisek w JEDNEJ linii — zamiast dwóch pięter w komórce.
 *
 * Dwie linie w każdym wierszu (nazwa + numer, rejestracja + model) robiły
 * wiersze wysokie na 55–95 px. Domyślnie dopisek (krótki numer) zostaje
 * widoczny, a skraca się główny tekst; `shrink="secondary"` odwraca to dla
 * długiego dopisku (opis zmiany w dzienniku). Pełna treść obu jest
 * w podpowiedzi.
 */
export const InlinePair = ({
  primary,
  secondary,
  maxWidth = 360,
  bold = true,
  shrink = 'primary',
}: {
  primary: string;
  secondary?: string | null;
  maxWidth?: CellWidth;
  bold?: boolean;
  shrink?: 'primary' | 'secondary';
}) => (
  <Tooltip title={secondary ? `${primary} · ${secondary}` : primary} enterDelay={400}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, maxWidth, minWidth: 0 }}>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: bold ? 600 : undefined,
          minWidth: 0,
          flexShrink: shrink === 'primary' ? 1 : 0,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ minWidth: 0, flexShrink: shrink === 'secondary' ? 1 : 0 }}
        >
          {secondary}
        </Typography>
      )}
    </Box>
  </Tooltip>
);

/** Liczba z jednostką, data, kwota — nie łamiemy jej w pół. */
export const NoWrap = ({ children }: { children: ReactNode }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
    {children}
  </Box>
);

/**
 * Data z godziną BEZ sekund — „28.09.2026, 10:34”.
 *
 * Sekundy nic planiście nie mówią, a `toLocaleString` bez opcji dopisuje je
 * zawsze i łamie komórkę na dwie linie. Format siedzi w `shared/dateTime.ts`
 * (jedno źródło dla list, szczegółów i paska synchronizacji); tu zostają
 * nazwy, których używają listy. Kolumna daty z sortowaniem: `DateTimeField`
 * z `shared/DateFields.tsx`.
 */
export const fmtDateTimeShort = (d?: string | null): string => dateTimeNoSeconds(d);

/**
 * Chwila bez sekund, z pełnym czasem w podpowiedzi — tam, gdzie sekunda
 * bywa dowodem (dziennik działań), ale nie musi zajmować miejsca w wierszu.
 */
export const DateTimeCell = ({ value }: { value?: string | null }) => {
  if (!value || Number.isNaN(new Date(value).getTime())) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={fullDateTime(value)} enterDelay={400}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {fmtDateTimeShort(value)}
      </Typography>
    </Tooltip>
  );
};

/**
 * Spółka jako znacznik z krajem — pełna nazwa w podpowiedzi.
 *
 * „MARATHON International Sp. z o.o. Sp. k.” zajmowała trzy linie w każdym
 * wierszu. Kraj rozróżnia obie spółki instancji, a lista floty pokazuje je
 * tak od początku. Bez kraju skracamy samą nazwę — nie zgadujemy kraju.
 */
export const CompanyChip = ({
  name,
  country,
}: {
  name?: string | null;
  country?: string | null;
}) => {
  const fullName = name && name.trim() !== '' ? name : null;
  const code = country && country.trim() !== '' ? country : null;
  if (!fullName && !code) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={fullName ?? code ?? ''} enterDelay={400}>
      <Chip
        size="small"
        variant="outlined"
        label={code ?? fullName}
        sx={{ maxWidth: 160 }}
      />
    </Tooltip>
  );
};

/**
 * Ogranicza szerokość znaczników w komórce listy.
 *
 * Znacznik z opisem („serwis, do 12.10.2026”, „dostępny — dziś jeszcze
 * 9 h jazdy”) potrafił rozepchnąć kolumnę. Skrócony kończy się
 * wielokropkiem; pełny opis musi nieść podpowiedź samego znacznika.
 */
export const ChipCell = ({ max, children }: { max: number; children: ReactNode }) => (
  <Box sx={{ whiteSpace: 'nowrap', '& .MuiChip-root': { maxWidth: max } }}>{children}</Box>
);

/**
 * Wygląd gęstej listy react-admina: nagłówki w jednej linii, niższe wiersze,
 * węższe odstępy między kolumnami. Klucze sortowania i filtrów zostają bez
 * zmian — to wyłącznie styl.
 */
export const compactDatagridSx = {
  '& .RaDatagrid-headerCell': { whiteSpace: 'nowrap' },
  '& .MuiTableCell-sizeSmall': { px: 1, py: 0.5 },
  '& .MuiTableCell-sizeSmall:first-of-type': { pl: 2 },
};
